import type {CSSProperties, ReactNode} from 'react';

import {createCx} from '@utils/cx';
import Icon from '../Icon';

import styles from './index.module.less';
import emptyStateImage from '@/assets/storyboard-media-modal/empty-state.png';
import errorStateImage from '@/assets/storyboard-media-modal/error-state.png';
import offlineStateImage from '@/assets/storyboard-media-modal/offline-state.png';
import loadingIcon from '@/assets/icons/loading.gif';
import smartLoadingIcon from '@/assets/icons/smart-loading.gif';
import Button from '@/components/common/Button';

const cx = createCx(styles);

export type StatusViewStatus = 'loading' | 'error' | 'success' | 'empty';

export type StatusViewSize = 'large' | 'medium' | 'small';

export type StatusViewImageType = 'empty' | 'error' | 'offline';

export type StatusViewLoadingTheme = 'colorful' | 'white';

export type StatusViewAppearance = 'component' | 'page';

export interface StatusViewProps {
    /** 当前状态：loading/error/empty 展示占位视图，success 渲染 children */
    status: StatusViewStatus;
    /**
     * 尺寸规范：
     * - loading：large / medium / small
     * - error/empty：large / small（传 medium 等同 small）
     */
    size?: StatusViewSize;
    /** loading 图标类型：spinning=旋转圆圈(固定24px)，sparkle=星星动画(跟随size) */
    loadingType?: 'spinning' | 'sparkle';
    /** 星星 loading 主题，仅 status=loading 且 loadingType=sparkle 时生效 */
    loadingTheme?: StatusViewLoadingTheme;
    /** 自定义 icon，不传则使用默认 */
    icon?: ReactNode;
    /** 展示形态：component=模块级小 icon，page=页面级 120px 图片槽位 */
    appearance?: StatusViewAppearance;
    /** 页面级状态图片类型，appearance="page" 时生效 */
    imageType?: StatusViewImageType;
    /** 标题文本 */
    title?: ReactNode;
    /** 标题自定义类名 */
    titleClassName?: string;
    /** 描述文本，颜色更浅 */
    description?: ReactNode;
    /** 重试回调，仅 error 态有效，传入后渲染重试按钮 */
    onRetry?: () => void;
    /** 重试按钮文案，默认 "重试" */
    retryText?: string;
    /** 重试按钮行内样式，可定制宽高等 */
    retryStyle?: CSSProperties;
    /**
     * 行内样式，传递给根节点。
     * 支持通过 CSS 变量覆盖内部尺寸：
     * - --status-content-top: 内容区距离容器顶部的距离
     * - --status-icon-size: 占位区宽高
     * - --status-icon-width: 页面级图片占位区宽度
     * - --status-icon-height: 页面级图片占位区高度
     * - --status-icon-gap: icon → title 间距
     * - --status-title-fz: title 字号
     * - --status-title-color: title 颜色
     * - --status-title-gap: title → description 间距
     * - --status-desc-fz: description 字号
     * - --status-desc-color: description 颜色
     * - --status-action-mt: description/title → button 间距
     */
    style?: CSSProperties;
    className?: string;
    children?: ReactNode;
    footer?: ReactNode;
}

const DefaultSpinningIcon = () => <span className={cx('spinning')} />;

const loadingIconMap: Record<StatusViewLoadingTheme, string> = {
    colorful: smartLoadingIcon,
    white: loadingIcon,
};

const DefaultLoadingIcon: React.FC<{loadingTheme: StatusViewLoadingTheme}> = ({loadingTheme}) => (
    <img className={cx('sparkle')} src={loadingIconMap[loadingTheme]} alt="" aria-hidden="true" />
);

const DefaultErrorIcon = () => (
    <Icon className={cx('errorIcon')} name="AijBasicTishihuise" size={20} />
);

const statusImageMap: Record<StatusViewImageType, string> = {
    empty: emptyStateImage,
    error: errorStateImage,
    offline: offlineStateImage,
};

const getDefaultImageType = (status: StatusViewStatus): StatusViewImageType =>
    status === 'empty' ? 'empty' : 'error';

const DefaultStatusImage: React.FC<{
    status: StatusViewStatus;
    imageType?: StatusViewImageType;
}> = ({status, imageType}) => (
    <img
        className={cx('statusImage')}
        src={statusImageMap[imageType ?? getDefaultImageType(status)]}
        alt=""
        aria-hidden="true"
    />
);

const renderDefaultIcon = ({
    status,
    loadingType,
    loadingTheme,
    shouldUsePageImage,
    imageType,
}: {
    status: StatusViewStatus;
    loadingType: StatusViewProps['loadingType'];
    loadingTheme: StatusViewLoadingTheme;
    shouldUsePageImage: boolean;
    imageType?: StatusViewImageType;
}) => {
    if (status === 'loading') {
        return loadingType === 'spinning' ? (
            <DefaultSpinningIcon />
        ) : (
            <DefaultLoadingIcon loadingTheme={loadingTheme} />
        );
    }

    if (shouldUsePageImage) {
        return <DefaultStatusImage status={status} imageType={imageType} />;
    }

    return <DefaultErrorIcon />;
};

/**
 * error / empty 状态下 size 只有 large / small 两档，medium 归为 small
 */
const normalizeErrorSize = (size: StatusViewSize): 'large' | 'small' =>
    size === 'large' ? 'large' : 'small';

const sizeClass = {large: 'sizeLarge', medium: 'sizeMedium', small: 'sizeSmall'} as const;
const statusClass = {
    loading: 'statusLoading',
    error: 'statusError',
    empty: 'statusError',
} as const;

// LoadMoreError 子组件
// 分页加载失败的行内状态，用于列表尾部
export interface LoadMoreErrorProps {
    /** 失败文案，默认 "加载失败，请重试" */
    text?: ReactNode;
    /** 重试回调 */
    onRetry?: () => void;
    className?: string;
}

const LoadMoreError: React.FC<LoadMoreErrorProps> = ({
    text = '加载失败，请重试',
    onRetry,
    className,
}) => (
    <div className={cx('loadMoreErrorRoot', className)}>
        <span>{text}</span>
        <Icon name="AijBasicChongxinshengcheng" size={12} onClick={onRetry} />
    </div>
);

interface StatusViewType extends React.FC<StatusViewProps> {
    LoadMoreError: typeof LoadMoreError;
}

const StatusView: StatusViewType = ({
    status,
    size = 'large',
    loadingType = 'sparkle',
    loadingTheme = 'colorful',
    icon,
    appearance = 'component',
    imageType,
    title,
    titleClassName,
    description,
    onRetry,
    retryText = '重试',
    retryStyle,
    style,
    className,
    children,
    footer,
}) => {
    if (status === 'success') {
        return children as React.ReactElement;
    }

    const effectiveSize = status === 'loading' ? size : normalizeErrorSize(size);
    const shouldUsePageImage = appearance === 'page' && status !== 'loading';
    const defaultIcon = renderDefaultIcon({
        status,
        loadingType,
        loadingTheme,
        shouldUsePageImage,
        imageType,
    });

    const showButton = status === 'error' && onRetry;
    const buttonSize = effectiveSize === 'large' ? 'middle' : 'mini';
    const hasContentTop = Boolean(style && '--status-content-top' in style);

    return (
        <div
            className={cx(
                'root',
                sizeClass[effectiveSize],
                statusClass[status],
                {
                    statusPage: shouldUsePageImage,
                    statusContentTop: hasContentTop,
                },
                className,
            )}
            style={style}
        >
            <div className={cx('content')}>
                {icon ?? <div className={cx('icon')}>{defaultIcon}</div>}
                {title && <div className={cx('title', titleClassName)}>{title}</div>}
                {description && <div className={cx('description')}>{description}</div>}
                {showButton && (
                    <div className={cx('action')}>
                        <Button size={buttonSize} style={retryStyle} onClick={onRetry}>
                            {retryText}
                        </Button>
                    </div>
                )}
                {footer}
            </div>
        </div>
    );
};

// 挂载子组件
StatusView.LoadMoreError = LoadMoreError;

export default StatusView;
